<template>
  <v-container class="px-10" fluid>
    <v-popup-loading :isLoading="isLoadingBranch" />
    <!-- Breadcrumbs -->
    <v-row>
      <v-col>
        <v-breadcrumbs
          :items="breadcrumbs"
          divider=">"
          class="px-0"
        ></v-breadcrumbs>
        <div class="d-flex justify-space-between">
          <h3 class="pageHeadline" t-data="page-headline">Hero Banners</h3>
          <v-btn
            id="add-btn"
            v-if="hasPermCreate()"
            large
            color="primary600Color"
            @click="create"
            class="white-text-button button-bold"
            t-data="create-btn"
          >
            <v-icon left>mdi-plus-circle-outline</v-icon>Create Hero Banner
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <v-alert-confirm
      :show="isDelete"
      :title="'Remove Confirmation'"
      :subTitle="subTitle"
      :close="closeDeletePopup"
      :headline-color="'black'"
      :buttonText="'Remove'"
      :confirm="confirmDelete"
      :icon="''"
    ></v-alert-confirm>

    <snack-bar v-model="isCreateSuccess" :content="snackbarSuccess"></snack-bar>
    <snack-bar
      v-model="isCreateFail"
      :content="snackbarFail"
      type="error"
    ></snack-bar>
    <preview
      :show="isPreview"
      :close-preview="setShowPreview"
      :data-object="heroBannerObject"
    />
    <edit-order-dialog
      :show="isEditOrder"
      :data="editOrderObject"
      :close="closeEditOrder"
      @save="saveEditOrder"
    ></edit-order-dialog>

    <!-- Data table -->
    <v-card>
      <v-card-title :class="{'mb-0': true, 'pb-0': isSelectedBranch}">
        <v-row>
          <v-col cols="12" sm="4" class="mt-0 pt-0 pb-0">
            <single-selected-branch
              outlined
              required
              dense
              title="Branch"
              :branch="orgList.all"
              :selectedBranch="selectedBranch"
              :syncData="changedSelectedBranch"
              :rule="v => !!v || 'Please select branch'"
            ></single-selected-branch>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-title class="pt-0" v-if="showSearchAndFilter">
        <v-row>
          <v-col class="pt-0 pb-0" cols="12" sm="4" t-data="search-panel">
            <search
              :key="componentKey"
              :search="search"
              label="Search by Title"
              @input="textSearch"
              @clear="clearSearch()"
            ></search>
          </v-col>
          <v-col class="pt-0 pb-0" cols="12" sm="6"></v-col>
          <v-col
            cols="12"
            sm="2"
            class="d-flex justify-end align-center pt-0 pb-0"
            t-data="filter-panel"
          >
            <filter-dropdown :key="componentKey" @apply="handleApplyFilter"></filter-dropdown>
          </v-col>
        </v-row>
      </v-card-title>
      <v-tabs
        v-if="isSelectedBranch"
        v-model="tab"
        class="status-tab fix-height-tab body-regular"
        color="primary500Color"
        slider-color="primary500Color"
        align-with-title
        @change="fetchByTab"
      >
        <v-tab class="fix-width-tab" v-for="item in tabItems" :key="item">
          {{ item }}
        </v-tab>
        <v-tab-item>
          <v-layout fluid v-resize="onResize">
            <v-data-table
              :loading="isLoading"
              :options.sync="options"
              :headers="table.headers"
              :items="heroBannerList"
              :items-per-page="table.pageSize"
              :server-items-length="total"
              :footer-props="{'items-per-page-options': table.itemPerPages}"
              :sort-desc="true"
              :must-sort="false"
              class="elevation-1 custom-table"
              fixed-header
              style="width: 100%"
              :height="
                windowSize.y - heightOptions.header - heightOptions.tableFooter
              "
            >
              <template v-slot:item.contents="{item}">
                <tooltip-title :contents="item.contents">
                  <template v-slot:content>
                    <v-list-item-content
                      class="d-inline-block text-truncate"
                      style="width: 130px"
                    >
                      <v-list-item-title>
                        {{
                          item.contents[0].code === 'th'
                            ? item.contents[0].title
                            : item.contents[1].title
                        }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{
                          item.contents[0].code === 'en'
                            ? item.contents[0].title
                            : item.contents[1].title
                        }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </tooltip-title>
              </template>
              <template v-slot:item.org="{item}">
                {{ item.org.org_codename.toUpperCase() }}</template
              >
              <template v-slot:item.type="{item}">{{
                capitalize(item.type)
              }}</template>
              <template v-slot:item.appear_on_page_type="{item}">{{
                appearOnText(item.appear_on_page_type)
              }}</template>
              <template v-slot:item.is_publish="{item}">
                <div class="d-flex justify-center">
                  <publish-status-chip :is-publish="item.is_publish" />
                </div>
              </template>
              <template v-slot:item.start_date="{item}">
                {{ !!item.start_date ? dateFmt(item.start_date) : '' }}
              </template>
              <template v-slot:item.end_date="{item}">
                {{ !!item.end_date ? dateFmt(item.end_date) : '' }}
              </template>
              <template v-slot:item.update_date="{item}">
                {{ !!item.update_date ? dateFmt(item.update_date) : '' }}
              </template>
              <template v-slot:item.update_by_user="{item}">
                <username-status-text :user="item.update_by_user" />
              </template>
              <template v-slot:item.status="{item}">
                <v-switch
                  t-data="status-switch"
                  color="primary200Color"
                  v-model="item.status"
                  :false-value="inActiveStatus"
                  :true-value="activeStatus"
                  :disabled="!hasPermEditByOrgs(item.org)"
                  @change="event => handleSwitchStatusChange(event, item.id)"
                  class="mb-0 pb-0 custom-switch"
                ></v-switch>
              </template>
              <template v-slot:item.actions="{item}">
                <v-icon
                  t-data="preview-action"
                  class="px-2 action-icon"
                  @click="preview(item)"
                  >mdi-magnify</v-icon
                >
                <v-icon
                  t-data="edit-action"
                  class="px-2 action-icon"
                  @click="rowClick(item)"
                  >mdi-pencil-outline</v-icon
                >
                <v-icon
                  v-if="hasPermDeleteByOrgs(item.org)"
                  t-data="delete-action"
                  class="px-2 action-icon"
                  @click="showDeletePopup(item.id)"
                  >mdi-trash-can-outline</v-icon
                >
                <v-icon
                  v-else
                  t-data="empty-icon"
                  class="px-2 action-icon transparent-icon"
                ></v-icon>
              </template>
            </v-data-table>
          </v-layout>
          <div
            class="table-footer-prepend"
            t-data="total-result-content"
            v-if="!isLoading"
          >
            <div class="vertical-center pl-4">
              <b>{{ total }}</b> Search results
            </div>
          </div>
        </v-tab-item>
        <v-tab-item>
          <sortable-table
            @updateSuccess="showSuccessSnackbar(actionEnumData.STATUS_UPDATED)"
            @updateFail="showFailSnackbar(actionEnumData.STATUS_UPDATED)"
          ></sortable-table>
        </v-tab-item>
      </v-tabs>
    </v-card>
  </v-container>
</template>

<script>
import {mapState, mapActions} from 'vuex'
import {
  capitalizeFirstLetter,
  convertDate,
  convertSelectedData,
  hasPermision,
  hasPermissionsByOrgs,
  searchHandler,
  snackbarFailContent,
  snackbarSuccessContent,
  swapArrayElements
} from '@/helper/helper'
import AlertConfirm from '@/components/AlertConfirm'
import FilterDropdown from '@/views/hero-banner/FilterDropdown'
import PopupLoading from '@/components/PopupLoading'
import Preview from '@/components/hero-banner/Preview'
import Search from '@/components/Search'
import SingleSelectedBranch from '@/components/SingleSelectedBranch'
import SnackBar from '@/components/SnackBar'
import EditOrderDialog from '@/components/EditOrderDialog'
import TooltipTitle from '@/components/TooltipTitle'
import {actionEnum, tabEnum} from '@/constants/constants'
import {
  appearOnPageTypeText,
  heroBannerStatusEnum
} from '@/constants/hero-banner'
import PublishStatusChip from '@/components/PublishStatusChip'
import UsernameStatusText from '@/components/UsernameStatusText'
import SortableTable from '@/components/hero-banner/SortableTable'

export default {
  components: {
    'v-alert-confirm': AlertConfirm,
    'v-popup-loading': PopupLoading,
    EditOrderDialog,
    FilterDropdown,
    Preview,
    PublishStatusChip,
    Search,
    SingleSelectedBranch,
    SnackBar,
    SortableTable,
    TooltipTitle,
    UsernameStatusText
  },
  data() {
    return {
      pageName: 'Hero banner',
      heightOptions: {
        header: 427,
        tableFooter: 59
      },
      windowSize: {
        x: 0,
        y: 0
      },
      componentKey: 0,
      selectedBranch: '',
      isCreateSuccess: false,
      isCreateFail: false,
      snackTitle: 'created',
      actionEnumData: actionEnum,
      buttonConfirmText: 'Delete',
      subTitle:
        'Are you sure you want to remove this Hero banner? This action will remove content in both Thai and English.',
      search: '',
      isUpdate: false,
      options: {},
      activeStatus: heroBannerStatusEnum.ACTIVE,
      inActiveStatus: heroBannerStatusEnum.INACTIVE,
      tab: 0,
      table: {
        page: 1,
        pageSize: 25,
        itemPerPages: [25, 50, 100],
        headers: [
          {
            text: 'Title',
            align: 'left',
            sortable: false,
            value: 'contents',
            width: 200
          },
          {
            text: 'Branch',
            align: 'left',
            value: 'org',
            sortable: false,
            width: 90
          },
          {
            text: 'Banner Type',
            align: 'left',
            value: 'type',
            sortable: false,
            width: 130
          },
          {
            text: 'Appear on',
            align: 'left',
            value: 'appear_on_page_type',
            sortable: false,
            width: 110
          },
          {
            text: 'Publish Status',
            align: 'center',
            value: 'is_publish',
            sortable: false,
            width: 140
          },
          {
            text: 'Publish Start Date/Time',
            align: 'left',
            value: 'start_date',
            sortable: false,
            width: 150
          },
          {
            text: 'Publish End Date/Time',
            align: 'left',
            value: 'end_date',
            sortable: false,
            width: 150
          },
          {
            text: 'Updated Date/Time',
            align: 'left',
            value: 'update_date',
            sortable: false,
            width: 196
          },
          {
            text: 'Updated By',
            align: 'left',
            value: 'update_by_user',
            sortable: false,
            width: 162
          },
          {
            text: 'Status',
            align: 'center',
            value: 'status',
            sortable: false,
            width: 50
          },
          {
            text: 'Actions',
            align: 'left',
            value: 'actions',
            sortable: false,
            width: 140
          }
        ]
      },
      breadcrumbs: [
        {
          text: 'Dashboard',
          exact: true,
          href: '/'
        },
        {
          text: 'Hero Banner',
          disabled: true,
          link: true,
          href: '/'
        }
      ]
    }
  },
  mounted() {
    let query = this.$route.query.isCreateSuccess
    let querySnackTitle = this.$route.query.snackTitle

    if (query) {
      this.$router.replace({})
      this.isCreateSuccess = query
      this.snackTitle = querySnackTitle
    }
  },
  computed: {
    isSelectedBranch() {
      return this.orgList.selected.length > 0
    },
    showSearchAndFilter() {
      return this.isSelectedBranch && this.tabItems[this.tab] === tabEnum.LIST
    },
    tabItems() {
      return Object.values(tabEnum)
    },
    snackbarSuccess() {
      return snackbarSuccessContent(this.pageName, this.snackTitle)
    },
    snackbarFail() {
      return snackbarFailContent(this.pageName, this.snackTitle)
    },
    ...mapState('heroBanner', [
      'editOrderObject',
      'heroBannerList',
      'heroBannerObject',
      'isDelete',
      'isEditOrder',
      'isLoading',
      'isLoadingBranch',
      'isPreview',
      'orgList',
      'sortableList',
      'total',
      'updateId'
    ])
  },
  async created() {
    this.setListOrgInfo()
    await this.fetchSections()
  },
  methods: {
    onResize() {
      this.windowSize = {x: window.innerWidth, y: window.innerHeight}
    },
    hasPermCreate() {
      return hasPermision('CanAddHeroBanner')
    },
    hasPermEditByOrgs(org) {
      const codenames = [org.org_codename]
      return hasPermissionsByOrgs('CanEditHeroBanner', codenames)
    },
    hasPermDeleteByOrgs(org) {
      const codenames = [org.org_codename]
      return hasPermissionsByOrgs('CanDeleteHeroBanner', codenames)
    },
    create() {
      this.$router.push({name: 'hero-banner-create'})
    },
    capitalize(text) {
      return capitalizeFirstLetter(text)
    },
    appearOnText(appearOnEnum) {
      return appearOnPageTypeText[appearOnEnum]
    },
    resetSearchAndFilterData() {
      this.options.page = 1
      this.componentKey++
      this.search = ''
      this.resetSearchAndFilter()
    },
    changedSelectedBranch(value) {
      if (value.length > 0) {
        this.orgList.selected = []
        this.orgList.selected = convertSelectedData(
          value,
          this.orgList.all,
          this.orgList.selected
        )
        this.fetchByTab(this.tab)
      }
    },
    async saveEditOrder(data) {
      this.setShowEditOrder(false)
      const {oldOrderNo, newOrderNo} = data
      const newOrderList = swapArrayElements(
        this.sortableList,
        oldOrderNo - 1,
        newOrderNo - 1
      )
      await this.reorderList(newOrderList)
      this.showSuccessSnackbar(actionEnum.ORDER_UPDATED)
    },
    clearSearch() {
      this.setTextSearch('')
      this.options.page = 1
      this.fetch()
    },
    async confirmDelete() {
      const isSuccess = await this.delete()
      if (isSuccess) {
        this.showSuccessSnackbar(actionEnum.REMOVED)
      } else {
        this.showFailSnackbar(actionEnum.REMOVED)
      }
    },
    closeDeletePopup() {
      this.setDeletePopup(false)
    },
    showDeletePopup(id) {
      this.setDeleteId(id)
      this.setDeletePopup(true)
    },
    async handleSwitchStatusChange(event, id) {
      this.setSelectedId(id)
      const isSuccess = await this.changeStatus(event)
      if (isSuccess) {
        this.showSuccessSnackbar(actionEnum.STATUS_UPDATED)
      } else {
        this.showFailSnackbar(actionEnum.STATUS_UPDATED)
      }
    },
    dateFmt: date => convertDate(date),
    textSearch(data) {
      this.search = data
      this.timer = searchHandler(this.timer, clearTimeout, async () => {
        this.setTextSearch(this.search.trim())
        this.options.page = 1
        await this.fetch()
      })
    },
    async fetchByTab(data) {
      this.resetSearchAndFilterData()
      if (this.tabItems[data] === tabEnum.SORT) {
        await this.fetchSortableList()
      }
      if (this.tabItems[data] === tabEnum.LIST) {
        await this.fetch()
      }
    },
    handleApplyFilter() {
      this.options.page = 1
    },
    async preview(row) {
      await this.setHeroBannerPreview(row.id)
    },
    rowClick(row) {
      this.$router.push({name: 'hero-banner-edit', params: {id: row.id}})
    },
    showSuccessSnackbar(action) {
      this.isCreateSuccess = true
      this.snackTitle = action
    },
    showFailSnackbar(action) {
      this.isCreateFail = true
      this.snackTitle = action
    },
    closeEditOrder() {
      this.setShowEditOrder(false)
    },
    ...mapActions('heroBanner', [
      'changeStatus',
      'delete',
      'fetch',
      'fetchSections',
      'fetchSortableList',
      'reorderList',
      'resetList',
      'resetSearchAndFilter',
      'setDeleteId',
      'setDeletePopup',
      'setFieldSort',
      'setHeroBannerPreview',
      'setListOrgInfo',
      'setPage',
      'setPageSize',
      'setSelectedId',
      'setShowEditOrder',
      'setShowPreview',
      'setSort',
      'setTextSearch'
    ])
  },
  beforeRouteLeave(to, from, next) {
    this.setTextSearch('')
    this.resetList()
    next()
  },

  watch: {
    options: {
      handler() {
        const {sortBy, sortDesc, page, itemsPerPage} = this.options
        let sort
        if (sortBy[0] === 'contents') {
          sort = 'title'
        } else if (sortBy[0] === 'org') {
          sort = 'org_codename'
        } else {
          sort = sortBy[0]
        }
        this.setPage(page)
        this.setFieldSort(sort)
        this.setPageSize(itemsPerPage)
        this.setSort(sortDesc[0])
        this.fetch()
      },
      deep: true
    }
  }
}
</script>
